import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import {
  HomologationItem,
  HomologationPair,
  HomologationSaveEvent,
} from '../../services/homologations.interfaces';
import { UploadZoneComponent } from '@angular-monorepo/ui';
import { TableModule } from 'primeng/table';
import { Panel } from 'primeng/panel';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'lib-homologation-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    UploadZoneComponent,
    TableModule,
    Panel,
    TranslatePipe,
  ],
  templateUrl: './homologation-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomologationFormComponent implements OnChanges {
  @Input() visible = false;
  @Input() homologation: HomologationItem | null = null;
  @Input() readonly = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() save = new EventEmitter<HomologationSaveEvent>();
  @ViewChild('uploadZone') uploadZone?: UploadZoneComponent;

  readonly MAX_RULES = 10;
  readonly allowedExtensions: string[] = ['.csv', '.xls', '.xlsx'];
  readonly allowedMimeTypes: string[] = [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  selectedFile: File | null = null;

  private readonly fb = inject(FormBuilder);
  form: FormGroup = this.fb.group({
    name: ['', Validators.required],
    rules: this.fb.array([]),
  });
  private importedFile: File | null = null;
  private hasInitialized = false;

  get rules(): FormArray {
    return this.form.get('rules') as FormArray;
  }

  get dialogTitle(): string {
    if (!this.homologation) return 'Nueva Homologación';
    if (this.readonly) return 'Ver Homologación';
    return 'Editar Homologación';
  }

  get isViewMode(): boolean {
    return this.readonly;
  }

  get isManualEditable(): boolean {
    if (this.readonly) return false;
    if (this.selectedFile) return false;
    if (!this.homologation) return true; // creación nueva

    const totalRules = Array.isArray(this.homologation.json_data)
      ? this.homologation.json_data.length
      : 1;

    return totalRules <= this.MAX_RULES;
  }

  get isTooManyRules(): boolean {
    if (this.readonly) return false;
    if (this.selectedFile) return false;
    if (!this.homologation?.json_data) return false;

    const totalRules = Array.isArray(this.homologation.json_data)
      ? this.homologation.json_data.length
      : 1;

    return totalRules > this.MAX_RULES;
  }

  get isImportMode(): boolean {
    return !!this.importedFile;
  }

  get rulesTableData(): HomologationPair[] {
    if (!this.homologation?.json_data) return [];

    return Array.isArray(this.homologation.json_data)
      ? this.homologation.json_data
      : [this.homologation.json_data];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true && !this.hasInitialized) {
      this.hasInitialized = true;

      this.selectedFile = null;
      this.importedFile = null;

      this.resetForm();
    }

    if (changes['visible']?.currentValue === false) {
      this.hasInitialized = false;
      this.clearUploadZone();
    }
  }

  addRule(rule?: HomologationPair): void {
    if (this.readonly) return;
    if (this.selectedFile) return;
    if (this.rules.length >= this.MAX_RULES) return;

    this.rules.push(
      this.fb.group({
        origin: [rule?.origin ?? null, Validators.required],
        destiny: [rule?.destiny ?? null, Validators.required],
      }),
    );
  }

  removeRule(index: number): void {
    if (this.readonly) return;
    this.rules.removeAt(index);
  }

  onFilesReady(files: File[]): void {
    const file = files?.[0];
    if (!file) return;

    this.selectedFile = file;
    this.importedFile = file;

    this.rules.clear();

    this.rules.disable({ emitEvent: false });

    if (!this.form.get('name')?.value) {
      this.form.patchValue({
        name: this.homologation?.name ?? `import ${new Date().toISOString()}`,
      });
    }

    this.form.updateValueAndValidity();
  }

  onFilesCleared(): void {
    console.log('[onFilesCleared]');

    // ❌ NO limpiar si no hay archivo seleccionado
    if (!this.selectedFile && !this.importedFile) {
      return;
    }

    this.selectedFile = null;
    this.importedFile = null;

    this.rules.enable({ emitEvent: false });

    if (this.rules.length === 0) {
      this.addRule();
    }

    this.form.updateValueAndValidity();
  }

  onSave(): void {
    if (this.readonly) {
      return;
    }

    const nameControl = this.form.get('name');
    const name = nameControl?.value;

    // IMPORT MODE (ARCHIVO)
    if (this.importedFile) {
      if (!name) {
        nameControl?.setErrors({ required: true });
        nameControl?.markAsTouched();
        return;
      }

      this.save.emit({
        mode: 'import',
        id: this.homologation?.id,
        payload: {
          name,
          file: this.importedFile,
        },
      });

      this.clearUploadZone();

      return;
    }

    // MANUAL MODE
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const rules = this.form.value.rules as HomologationPair[];

    this.save.emit({
      mode: 'manual',
      id: this.homologation?.id,
      payload: {
        name,
        json_data: rules.length === 1 ? rules[0] : rules,
      },
    });
    this.clearUploadZone();
  }

  onCancel(): void {
    this.clearUploadZone();
    this.visibleChange.emit(false);
    this.cancelled.emit();
  }

  private resetForm(): void {
    this.rules.clear();
    this.form.reset({ name: '' });

    if (!this.homologation) {
      this.addRule();
      return;
    }

    this.form.patchValue({ name: this.homologation.name });

    const rules = Array.isArray(this.homologation.json_data)
      ? this.homologation.json_data
      : [this.homologation.json_data];

    if (rules.length > this.MAX_RULES) {
      return;
    }

    rules.forEach((r) => this.addRule(r));
  }

  private clearUploadZone(): void {
    this.selectedFile = null;
    this.importedFile = null;

    this.rules.enable({ emitEvent: false });

    this.uploadZone?.clearFiles();

    if (this.rules.length === 0 && !this.readonly) {
      this.addRule();
    }

    this.form.updateValueAndValidity();
  }
}
